/**
 * Service Container
 * 
 * A minimal DI container for resolving services in widget constructors.
 * Uses class constructors as tokens for type-safe injection.
 * 
 * Usage:
 * ```ts
 * // Registration (during kendo.core.js init)
 * serviceContainer.singleton(UtilsService);
 * serviceContainer.scoped(SomeScopedService);
 * 
 * // Or register an existing instance
 * serviceContainer.singletonInstance(UtilsService, existingUtilsService);
 * 
 * // In widgets - use inject() with default parameter
 * class Chat extends Widget {
 *     constructor(
 *         element: HTMLElement,
 *         options: ChatOptions,
 *         utils = inject(UtilsService)
 *     ) {
 *         super(element, options);
 *         this.utils = utils;
 *     }
 * }
 * ```
 */

import { 
    ServiceLifetime, 
    Constructor, 
    IServiceContainer, 
    IServiceRegistration 
} from "./models/dependency-container";

class ServiceContainer implements IServiceContainer {
    /** Map from constructor to registration */
    private registrations = new Map<Constructor, IServiceRegistration>();

    /**
     * Register a class as a singleton service.
     * One instance will be created and shared everywhere.
     */
    singleton<T>(ctor: Constructor<T>, factory?: () => T): void {
        this.registrations.set(ctor, {
            ctor,
            factory: factory ?? (() => new ctor()),
            lifetime: ServiceLifetime.Singleton,
            instance: undefined
        });
    }

    /**
     * Register an existing instance as a singleton.
     * Useful when the instance is already created (e.g., during kendo.core.js init).
     */
    singletonInstance<T>(ctor: Constructor<T>, instance: T): void {
        this.registrations.set(ctor, {
            ctor,
            factory: () => instance,
            lifetime: ServiceLifetime.Singleton,
            instance
        });
    }

    /**
     * Register a class as a scoped service.
     * A new instance will be created each time it's resolved.
     */
    scoped<T>(ctor: Constructor<T>, factory?: () => T): void {
        this.registrations.set(ctor, {
            ctor,
            factory: factory ?? (() => new ctor()),
            lifetime: ServiceLifetime.Scoped,
            instance: undefined
        });
    }

    /**
     * Resolve a service by its class constructor.
     * - Singleton: returns cached instance (creates on first call)
     * - Scoped: creates new instance each time
     */
    resolve<T>(ctor: Constructor<T>): T {
        const registration = this.registrations.get(ctor);
        
        if (!registration) {
            throw new Error(`Service ${ctor.name} is not registered`);
        }

        if (registration.lifetime === ServiceLifetime.Singleton) {
            // Return cached instance or create and cache
            if (registration.instance === undefined) {
                registration.instance = registration.factory();
            }
            return registration.instance as T;
        }

        // Scoped: always create new instance
        return registration.factory() as T;
    }

    /**
     * Try to resolve a service, returns undefined if not registered.
     */
    tryResolve<T>(ctor: Constructor<T>): T | undefined {
        if (!this.has(ctor)) {
            return undefined;
        }
        return this.resolve(ctor);
    }

    /**
     * Check if a service is registered.
     */
    has<T>(ctor: Constructor<T>): boolean {
        return this.registrations.has(ctor);
    }

    /**
     * Get the lifetime of a registered service.
     */
    getLifetime<T>(ctor: Constructor<T>): ServiceLifetime | undefined {
        return this.registrations.get(ctor)?.lifetime;
    }
}

/** Global service container instance */
export const serviceContainer = new ServiceContainer();

/**
 * Inject a service by its class constructor.
 * Use as default parameter value in constructors.
 * 
 * @example
 * class Chat extends Widget {
 *     constructor(
 *         element: HTMLElement,
 *         options: ChatOptions,
 *         utils = inject(UtilsService)
 *     ) {
 *         this.utils = utils;
 *     }
 * }
 */
export function inject<T>(ctor: Constructor<T>): T {
    return serviceContainer.resolve(ctor);
}
